// Mock O*NET service for development when API key is not available
class MockOnetService {
  constructor() {
    this.baseURL = '/api/onet';
    this.clientId = 'careerlift_skillbind';
    this.apiKey = 'mock-key-for-development';
  }

  // Mock occupation data
  getMockOccupations() {
    return [
      {
        title: 'Software Developers, Applications',
        soc_code: '15-1132',
        description: 'Develop, create, and modify general computer applications software or specialized utility programs.',
        skills: [
          { name: 'Programming', importance: 5, level: 4.5 },
          { name: 'Problem Solving', importance: 5, level: 4.2 },
          { name: 'Critical Thinking', importance: 4, level: 4.0 }
        ],
        education_level: 'bachelors',
        experience_level: 'mid',
        growth_outlook: 'very_high',
        job_growth_rate: 22.2
      },
      {
        title: 'Web Developers',
        soc_code: '15-1134',
        description: 'Design and create websites.',
        skills: [
          { name: 'Web Development', importance: 5, level: 4.8 },
          { name: 'HTML/CSS', importance: 5, level: 4.6 },
          { name: 'JavaScript', importance: 4, level: 4.3 }
        ],
        education_level: 'bachelors',
        experience_level: 'mid',
        growth_outlook: 'high',
        job_growth_rate: 13.0
      },
      {
        title: 'Computer Systems Analysts',
        soc_code: '15-1121',
        description: 'Analyze science, engineering, business, and other data processing problems to implement and improve computer systems.',
        skills: [
          { name: 'Systems Analysis', importance: 5, level: 4.7 },
          { name: 'Problem Solving', importance: 5, level: 4.5 },
          { name: 'Communication', importance: 4, level: 4.2 }
        ],
        education_level: 'bachelors',
        experience_level: 'mid',
        growth_outlook: 'moderate',
        job_growth_rate: 7.0
      }
    ];
  }

  async searchOccupations(keyword, limit = 20) {
    console.log(`Mock O*NET: Searching for "${keyword}" (limit: ${limit})`);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const allOccupations = this.getMockOccupations();
    const filtered = allOccupations.filter(occ => 
      occ.title.toLowerCase().includes(keyword.toLowerCase()) ||
      occ.description.toLowerCase().includes(keyword.toLowerCase())
    );
    
    return {
      success: true,
      data: {
        occupations: filtered.slice(0, limit),
        total: filtered.length,
        keyword: keyword
      }
    };
  }

  async getOccupationDetails(socCode, contentType = 'details') {
    console.log(`Mock O*NET: Getting details for SOC ${socCode}`);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const occupations = this.getMockOccupations();
    const occupation = occupations.find(occ => occ.soc_code === socCode);
    
    if (!occupation) {
      return {
        success: false,
        error: `Occupation with SOC code ${socCode} not found`
      };
    }
    
    return {
      success: true,
      data: {
        career: occupation,
        lastUpdated: new Date().toISOString()
      }
    };
  }

  async getOccupationCategories() {
    console.log('Mock O*NET: Getting occupation categories');
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return {
      success: true,
      data: {
        categories: [
          { id: '1', name: 'Computer and Mathematical Occupations' },
          { id: '2', name: 'Business and Financial Operations' },
          { id: '3', name: 'Architecture and Engineering' },
          { id: '4', name: 'Life, Physical, and Social Science' },
          { id: '5', name: 'Community and Social Service' }
        ]
      }
    };
  }

  // Add other mock methods as needed
  async getOccupationSkills(socCode) {
    const occupation = this.getMockOccupations().find(occ => occ.soc_code === socCode);
    return {
      success: true,
      data: {
        skills: occupation?.skills || []
      }
    };
  }

  async getOccupationKnowledge(socCode) {
    return {
      success: true,
      data: {
        knowledge: [
          { name: 'Computers and Electronics', importance: 5, level: 4.5 },
          { name: 'Mathematics', importance: 4, level: 4.0 },
          { name: 'English Language', importance: 4, level: 3.8 }
        ]
      }
    };
  }

  async getOccupationWorkActivities(socCode) {
    return {
      success: true,
      data: {
        work_activities: [
          { name: 'Analyzing Data or Information', importance: 5, level: 4.5 },
          { name: 'Thinking Creatively', importance: 4, level: 4.2 },
          { name: 'Making Decisions and Solving Problems', importance: 5, level: 4.7 }
        ]
      }
    };
  }

  async getOccupationWorkContext(socCode) {
    return {
      success: true,
      data: {
        work_context: [
          { name: 'Electronic Mail', importance: 5, level: 4.8 },
          { name: 'Face-to-Face Discussions', importance: 4, level: 3.5 },
          { name: 'Telephone', importance: 3, level: 3.2 }
        ]
      }
    };
  }

  async getOccupationEducation(socCode) {
    const occupation = this.getMockOccupations().find(occ => occ.soc_code === socCode);
    return {
      success: true,
      data: {
        education: {
          education_level: occupation?.education_level || 'bachelors',
          experience_required: occupation?.experience_level || 'mid'
        }
      }
    };
  }

  async getOccupationInterests(socCode) {
    return {
      success: true,
      data: {
        interests: [
          { name: 'Investigative', importance: 5, level: 4.5 },
          { name: 'Realistic', importance: 4, level: 4.0 },
          { name: 'Artistic', importance: 3, level: 3.5 }
        ]
      }
    };
  }

  async getOccupationWorkValues(socCode) {
    return {
      success: true,
      data: {
        work_values: [
          { name: 'Achievement', importance: 5, level: 4.5 },
          { name: 'Independence', importance: 4, level: 4.2 },
          { name: 'Recognition', importance: 4, level: 3.8 }
        ]
      }
    };
  }

  async getOccupationJobOutlook(socCode) {
    const occupation = this.getMockOccupations().find(occ => occ.soc_code === socCode);
    return {
      success: true,
      data: {
        job_outlook: {
          growth_rate: occupation?.job_growth_rate || 5.0,
          outlook: occupation?.growth_outlook || 'moderate'
        }
      }
    };
  }

  async getRelatedOccupations(socCode) {
    return {
      success: true,
      data: {
        related: [
          { title: 'Software Quality Assurance Engineers and Testers', soc_code: '15-1133' },
          { title: 'Computer Programmers', soc_code: '15-1131' },
          { title: 'Database Administrators', soc_code: '15-1141' }
        ]
      }
    };
  }

  async getOccupationsByCategory(categoryId) {
    return {
      success: true,
      data: {
        occupations: this.getMockOccupations()
      }
    };
  }

  async getComprehensiveOccupationData(socCode) {
    const occupation = this.getMockOccupations().find(occ => occ.soc_code === socCode);
    
    if (!occupation) {
      return {
        success: false,
        error: `Occupation with SOC code ${socCode} not found`
      };
    }

    return {
      success: true,
      data: {
        details: { career: occupation },
        skills: { skills: occupation.skills },
        knowledge: { knowledge: [] },
        workActivities: { work_activities: [] },
        workContext: { work_context: [] },
        education: { education: { education_level: occupation.education_level } },
        interests: { interests: [] },
        workValues: { work_values: [] },
        jobOutlook: { job_outlook: { growth_rate: occupation.job_growth_rate } },
        lastUpdated: new Date().toISOString()
      }
    };
  }

  processOnetDataForDatabase(onetData) {
    // Mock implementation - return the data as-is for now
    return onetData;
  }
}

export default new MockOnetService();
